import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import confetti from 'canvas-confetti';
import moment from 'moment-timezone';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { BehaviorSubject, Observable, catchError, lastValueFrom, throwError } from 'rxjs';

import { EventTypes, SignUpSteps } from '@site-mate/dashpivot-shared-library';

import { AuthService } from 'app/auth/auth.service';
import { IPasswordValidation } from 'app/shared/model/password-validation.model';
import { SignUp } from 'app/shared/model/sign-up.model';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { EventsService } from 'app/shared/service/events/events.service';
import Countries from 'app/user/shared/countries.json';
import MappedTimeZones from 'app/user/shared/mappedTimezones.json';

import { SignupErrorMessages } from './model/sign-up-error-messages';
import { SignupResponse } from './model/signup-response.model';
import { UserService } from '../user.service';

@Injectable({
  providedIn: 'root',
})
export class SignUpService {
  private activeStepSubject = new BehaviorSubject<SignUpSteps>(SignUpSteps.UserInfo);
  activeStep$ = this.activeStepSubject.asObservable();

  private queryParamSubject = new BehaviorSubject<Params>({});
  queryParams$ = this.queryParamSubject.asObservable();

  private signUpResponseSubject = new BehaviorSubject<{ teamId: string; templateId: string }>({
    teamId: '',
    templateId: '',
  });
  signUpResponse$ = this.queryParamSubject.asObservable();

  private signUpModel = new BehaviorSubject<SignUp>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    mobile: '',
    companyName: '',
    colorCode: '#2E81DA',
    isReCaptchaV3: false,
    vertical: [],
    role: [],
    reCaptchaResponse: '',
    reCaptchaSubmission: {
      v2: false,
      v3: false,
    },
    isSSOSignUp: false,
    continent: '',
  });
  signupModel$ = this.signUpModel.asObservable();

  private isProcessingSubject = new BehaviorSubject<boolean>(false);
  isProcessing$ = this.isProcessingSubject.asObservable();

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly userService: UserService,
    private readonly eventsService: EventsService,
    private readonly reCaptchaV3Service: ReCaptchaV3Service,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly errorHandler: ErrorHandler,
  ) {}

  async onSubmit() {
    try {
      this.isProcessingSubject.next(true);
      await this.reCaptchaV3Verification();

      this.updateSignupModel({
        ...this.getQueryParams(),
        isReCaptchaV3: !this.getSignUpModel().reCaptchaSubmission.v2,
        referrer: this.document.referrer,
      });

      const signUpResponse = await this.submitSignUpModel();
      await this.triggerPostSignUpActions(signUpResponse);
    } catch (error) {
      this.signupErrorHandling(error);
    } finally {
      this.isProcessingSubject.next(false);
    }
  }

  private async triggerPostSignUpActions(signUpResponse: SignupResponse) {
    await this.authService.storeAuthTokens(signUpResponse.loginData);

    this.eventsService.trackEvent(EventTypes.SignupFormSubmitted);
    void this.router.navigate(['/', 'home']);
  }

  private async submitSignUpModel(): Promise<SignupResponse> {
    const signUpResponse = await lastValueFrom(this.userService.signUp(this.getSignUpModel()));
    this.setSignUpResponse({
      templateId: signUpResponse.signUpTemplateId,
      teamId: signUpResponse.teamId,
    });
    return signUpResponse;
  }

  private async reCaptchaV3Verification() {
    if (!this.getSignUpModel().reCaptchaSubmission.v3) {
      this.updateSignupModel({
        reCaptchaResponse: await lastValueFrom(this.getRecaptchaToken()),
        reCaptchaSubmission: {
          v3: true,
          v2: false,
        },
      });
    }
  }

  getRecaptchaToken(): Observable<string> {
    const signupAction = 'signup';
    return this.reCaptchaV3Service.execute(signupAction).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  private signupErrorHandling(error: any) {
    const errorMessage = error.error?.message;
    const isReCaptchaError =
      errorMessage === SignupErrorMessages.ReCaptchaThresholdNotMet ||
      errorMessage === SignupErrorMessages.ReCaptchaBrowserError;
    if (isReCaptchaError) {
      this.eventsService.trackEvent(EventTypes.SignUpHumanVerificationRequired);
      this.setActiveStep(SignUpSteps.HumanVerification);
      return;
    }

    this.eventsService.trackEvent(EventTypes.SignupFormError);
    this.navigateBackOnError(error);
  }

  private navigateBackOnError(error: Error) {
    this.errorHandler.handle(error, {
      email: this.signUpModel.value?.email,
      mobile: this.signUpModel.value?.mobile,
      source: 'self sign up',
    });

    const delayNavigationToShowError = 3000;
    setTimeout(() => {
      void this.router.navigate(['/']);
    }, delayNavigationToShowError);
  }

  submitSignUpPageVisitedEvents(): void {
    const isTemplateReferral = this.getQueryParams().templateId;

    if (isTemplateReferral) {
      this.eventsService.trackEvent(EventTypes.SignUpPageVisited, {
        Context: 'Template Referral',
        UserContinent: this.initialUserContinent,
      });
    } else {
      this.eventsService.trackEvent(EventTypes.SignUpPageVisited, {
        UserContinent: this.initialUserContinent,
      });
    }
  }

  private confettiPop(particleRatio: number, opts: any): void {
    const confettiCount = 200;
    const colors = ['#3EC5D6', '#F6BB2D', '#F99226', '#6575BD'];
    const modalZIndex = 1050;

    confetti({
      origin: { y: 0.35 },
      colors,
      particleCount: Math.floor(confettiCount * particleRatio),
      zIndex: modalZIndex + 1,
      ...opts,
    });
  }

  popConfetti(): void {
    this.confettiPop(1, {
      spread: 26,
      startVelocity: 55,
    });
    this.confettiPop(0.2, {
      spread: 60,
    });
    this.confettiPop(0.35, {
      spread: 100,
      decay: 0.91,
      scalar: 0.8,
    });
    this.confettiPop(0.1, {
      spread: 120,
      startVelocity: 25,
      decay: 0.92,
      scalar: 1.2,
    });
    this.confettiPop(0.1, {
      spread: 120,
      startVelocity: 45,
    });
  }

  get isTemplateReferral(): boolean {
    const queryParams = this.getQueryParams();
    const isSitemateUseCaseReferral =
      queryParams?.reference?.includes('app') || queryParams?.reference?.includes('software');

    return queryParams.templateId && !isSitemateUseCaseReferral;
  }

  get isNonTemplateReferral(): boolean {
    const queryParams = this.getQueryParams();
    const isSitemateUseCaseReferral =
      queryParams?.reference?.includes('app') || queryParams?.reference?.includes('software');

    return !queryParams.templateId || !!isSitemateUseCaseReferral;
  }

  setQueryParams(params: Params) {
    this.queryParamSubject.next(params);
  }

  getQueryParams(): Params {
    return this.queryParamSubject.value;
  }

  setSignUpResponse(response: { teamId: string; templateId: string }) {
    this.signUpResponseSubject.next({ ...this.signUpResponseSubject.value, ...response });
  }

  getSignUpResponse() {
    return this.signUpResponseSubject.value;
  }

  updateSignupModel(info: object): void {
    this.signUpModel.next({ ...this.signUpModel.value, ...info });
  }

  getSignUpModel(): SignUp {
    return this.signUpModel.value;
  }

  setActiveStep(step: SignUpSteps): void {
    this.activeStepSubject.next(step);
  }

  getCurrentStep(): SignUpSteps {
    return this.activeStepSubject.value;
  }

  setProcessing(isProcessing: boolean): void {
    this.isProcessingSubject.next(isProcessing);
  }

  get userCountry(): string {
    const userTimeZone = (moment as any).tz.guess(true);
    return MappedTimeZones[userTimeZone]?.mcode;
  }

  get initialUserContinent(): string {
    return Countries.find((country) => country.value === this.userCountry)?.continentCode;
  }

  validatePassword(password: string): IPasswordValidation {
    const minLength = 10;
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasMinLength = password.length >= minLength;

    return {
      hasLowercase,
      hasUppercase,
      hasNumber,
      hasMinLength,
    };
  }

  getCombinedPasswordValidationRegex(): RegExp {
    return /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).{10,}$/;
  }
}
